'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ExternalLink,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Check,
  AlertTriangle,
  X,
  Unplug,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { trpc } from '@/lib/api/trpc';
import { cn } from '@/lib/utils';
import { buildShopifyOAuthAuthorizeUrl } from '@/lib/utils/shopify-oauth-utils';
import { ShopifyCredentialsBanner } from './shopify-credentials-banner';
import { ShopifyScopeUpdateTutorial } from './shopify-scope-update-tutorial';
import { ShopifySetupTutorial } from './shopify-setup-tutorial';

// ============================================================================
// Types & Schema
// ============================================================================

const normalizeShopifyShopUrl = (input: string): string =>
  input
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '')
    .toLowerCase();

const isShopifyShopUrl = (input: string): boolean =>
  /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(normalizeShopifyShopUrl(input));

const createShopifyFormSchema = (t: (key: string) => string) =>
  z.object({
    shopUrl: z
      .string()
      .min(1, t('shopify.validation.shopUrlRequired'))
      .refine(isShopifyShopUrl, t('shopify.validation.shopUrlInvalid')),
    apiToken: z.string().min(1, t('shopify.validation.apiTokenRequired')),
    apiKey: z.string().optional(),
    apiSecretKey: z.string().optional(),
    // OAuth App credentials (per-store Shopify Public App)
    shopifyAppClientId: z.string().optional(),
    shopifyAppClientSecret: z.string().optional(),
    syncOrders: z.boolean(),
    syncPresentmentCurrencies: z.boolean(),
    sendReceipt: z.boolean(),
    sendFulfillmentReceipt: z.boolean(),
    sendRebillReceipt: z.boolean(),
    sendRebillFulfillmentReceipt: z.boolean(),
    dynamicDiscountEnabled: z.boolean(),
    dynamicDiscountStrategy: z.enum(['order-level', 'line-item-allocation']),
    dynamicSellingPlanEnabled: z.boolean(),
    dynamicPriceSet: z.boolean(),
    autoSyncEnabled: z.boolean(),
    enableWetracked: z.boolean(),
    syncInventory: z.boolean(),
    useGraphqlOrderInjection: z.boolean(),
  });

type FormValues = z.infer<ReturnType<typeof createShopifyFormSchema>>;

type ShopifyCredentials = Partial<FormValues> | undefined;

const toShopifyFormValues = (credentials: ShopifyCredentials): FormValues => ({
  shopUrl: credentials?.shopUrl ?? '',
  apiToken: credentials?.apiToken ?? '',
  apiKey: credentials?.apiKey ?? '',
  apiSecretKey: credentials?.apiSecretKey ?? '',
  shopifyAppClientId: credentials?.shopifyAppClientId ?? '',
  shopifyAppClientSecret: credentials?.shopifyAppClientSecret ?? '',
  syncOrders: credentials?.syncOrders ?? true,
  syncPresentmentCurrencies: credentials?.syncPresentmentCurrencies ?? true,
  sendReceipt: credentials?.sendReceipt ?? true,
  sendFulfillmentReceipt: credentials?.sendFulfillmentReceipt ?? false,
  sendRebillReceipt: credentials?.sendRebillReceipt ?? false,
  sendRebillFulfillmentReceipt: credentials?.sendRebillFulfillmentReceipt ?? false,
  dynamicDiscountEnabled: credentials?.dynamicDiscountEnabled ?? false,
  dynamicDiscountStrategy: credentials?.dynamicDiscountStrategy ?? 'order-level',
  dynamicSellingPlanEnabled: credentials?.dynamicSellingPlanEnabled ?? false,
  dynamicPriceSet: credentials?.dynamicPriceSet ?? false,
  autoSyncEnabled: credentials?.autoSyncEnabled ?? false,
  enableWetracked: credentials?.enableWetracked ?? false,
  syncInventory: credentials?.syncInventory ?? false,
  useGraphqlOrderInjection: credentials?.useGraphqlOrderInjection ?? false,
});

const getCredentialsContentKey = (credentials: ShopifyCredentials): string =>
  JSON.stringify({
    shopUrl: credentials?.shopUrl ?? '',
    apiToken: credentials?.apiToken ?? '',
  });

interface ShopifyFormProps {
  defaultValues?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => Promise<void>;
  onDisconnect?: () => Promise<void>;
  isLoading?: boolean;
  storeId?: string;
  accountId?: string;
  orgSlug?: string;
}

// ============================================================================
// Component
// ============================================================================

export function ShopifyForm({
  defaultValues = {},
  onSubmit,
  onDisconnect,
  isLoading = false,
  storeId,
  accountId,
  orgSlug,
}: ShopifyFormProps) {
  const { t } = useTranslation('integrations');
  const trpcUtils = trpc.useUtils();
  const [isShopifyScriptPlugged, setIsShopifyScriptPlugged] = React.useState(false);
  const [showManualSetup, setShowManualSetup] = React.useState(false);
  const [oauthShopUrl, setOauthShopUrl] = React.useState('');
  const [oauthClientId, setOauthClientId] = React.useState('');
  const [oauthClientSecret, setOauthClientSecret] = React.useState('');
  const [isDisconnecting, setIsDisconnecting] = React.useState(false);
  const [showScopeAudit, setShowScopeAudit] = React.useState(false);
  const [isUnplugging, setIsUnplugging] = React.useState(false);
  const [isOAuthConnecting, setIsOAuthConnecting] = React.useState(false);
  const [isScopeUpdateModalOpen, setIsScopeUpdateModalOpen] = React.useState(false);

  // Check if script is installed
  const checkScriptQuery = trpc.shopifyScript.checkScript.useQuery(
    { storeId: storeId! },
    {
      enabled: Boolean(storeId) && Boolean(defaultValues.credentials?.shopUrl),
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
    },
  );

  React.useEffect(() => {
    if (checkScriptQuery.data?.isInjected) {
      setIsShopifyScriptPlugged(true);
    }
  }, [checkScriptQuery.data?.isInjected]);

  // Remove script mutation
  const removeScriptMutation = trpc.shopifyScript.removeScript.useMutation({
    onSuccess: () => {
      toast.success(t('shopify.toasts.scriptRemoved'));
      setIsShopifyScriptPlugged(false);
      trpcUtils.shopifyScript.checkScript.invalidate({ storeId: storeId! });
    },
    onError: (error) => {
      toast.error(error.message || t('shopify.toasts.removeScriptFailed'));
    },
  });

  const handleUnplugScript = async () => {
    if (!storeId) return;
    setIsUnplugging(true);
    try {
      await removeScriptMutation.mutateAsync({ storeId });
    } finally {
      setIsUnplugging(false);
    }
  };

  // Check scopes query
  const checkScopesQuery = trpc.integrations.checkShopifyScopes.useQuery(
    { storeId: storeId! },
    {
      enabled: false,
      retry: false,
    },
  );

  const runScopeAudit = async ({ silent }: { silent: boolean }) => {
    if (!storeId) {
      if (!silent) toast.error(t('shopify.toasts.saveFirst'));
      return;
    }
    setShowScopeAudit(true);
    const result = await checkScopesQuery.refetch();
    if (result.data?.status === 'missing_scopes' && result.data.missing.length > 0) {
      setIsScopeUpdateModalOpen(true);
    }
  };

  const handleAuditScopes = () => {
    void runScopeAudit({ silent: false });
  };

  // OAuth is always enabled now (per-store credentials)
  const isOAuthEnabled = true;

  const form = useForm<FormValues>({
    resolver: zodResolver(createShopifyFormSchema(t)),
    defaultValues: toShopifyFormValues(defaultValues.credentials),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = form;

  // Content-stable key — ignore new object identity from React Query refetches
  const credentialsKey = getCredentialsContentKey(defaultValues.credentials);
  const credentialsRef = React.useRef(defaultValues.credentials);
  credentialsRef.current = defaultValues.credentials;
  const previousShopUrlRef = React.useRef(defaultValues.credentials?.shopUrl ?? '');

  // Reset RHF when credential *content* changes (connect/disconnect), not on referential churn.
  // Clearing OAuth draft fields only on connected → disconnected — otherwise a window-focus
  // refetch rebuilds `credentials: {}` and wipes Client ID / Secret mid paste.
  React.useEffect(() => {
    const credentials = credentialsRef.current;
    reset(toShopifyFormValues(credentials));

    const nextShopUrl = credentials?.shopUrl ?? '';
    const previousShopUrl = previousShopUrlRef.current;
    previousShopUrlRef.current = nextShopUrl;

    if (!previousShopUrl || nextShopUrl) {
      return;
    }

    setOauthShopUrl('');
    setOauthClientId('');
    setOauthClientSecret('');
  }, [credentialsKey, reset]);

  const handleFormSubmit = async (data: FormValues) => {
    const credentials = data.useGraphqlOrderInjection ? { ...data, syncInventory: false } : data;
    await onSubmit({ credentials });
  };

  const connectSession = trpc.integrations.createShopifyConnectSession.useMutation({
    onSuccess: ({ sessionId }) => {
      window.location.href = buildShopifyOAuthAuthorizeUrl(sessionId);
    },
    onError: (error) => {
      setIsOAuthConnecting(false);
      toast.error(error.message);
    },
  });

  // The pair goes to the server in a POST; the URL Shopify sees carries only the session id.
  const handleConnectWithOAuth = () => {
    if (!storeId || !oauthShopUrl || !oauthClientId || !oauthClientSecret) {
      return;
    }
    setIsOAuthConnecting(true);
    connectSession.mutate({
      storeId,
      shop: normalizeShopifyShopUrl(oauthShopUrl),
      clientId: oauthClientId,
      clientSecret: oauthClientSecret,
      orgSlug: orgSlug || undefined,
    });
  };

  const savedShopUrl = defaultValues?.credentials?.shopUrl as string | undefined;
  const savedShopifyAppClientId = defaultValues?.credentials?.shopifyAppClientId as string | undefined;
  // Read masked: it only tells that the store has its own app, the server reads the secret itself.
  const savedShopifyAppClientSecret = defaultValues?.credentials?.shopifyAppClientSecret as
    | string
    | undefined;
  const canUpdateOAuthIntegration = Boolean(
    savedShopUrl && savedShopifyAppClientId && savedShopifyAppClientSecret,
  );

  const handleUpdateIntegration = () => {
    if (!storeId || !canUpdateOAuthIntegration) return;
    setIsOAuthConnecting(true);
    connectSession.mutate({ storeId, orgSlug: orgSlug || undefined });
  };

  const oauthShopUrlInvalid = oauthShopUrl.length > 0 && !isShopifyShopUrl(oauthShopUrl);

  const dynamicDiscountEnabled = watch('dynamicDiscountEnabled');
  const dynamicSellingPlanEnabled = watch('dynamicSellingPlanEnabled');
  const dynamicPriceSet = watch('dynamicPriceSet');
  const autoSyncEnabled = watch('autoSyncEnabled');
  const watchedShopUrl = watch('shopUrl');
  const watchedApiToken = watch('apiToken');

  // Use watched values for connection status (updates immediately on reset)
  const isConnected = Boolean(watchedShopUrl && watchedApiToken);

  // Check if connected via OAuth (has metadata from OAuth)
  const connectedViaOAuth = Boolean(defaultValues?.metadata?.connectedAt);
  // Use watched shopUrl for display name (falls back to metadata if available)
  const connectedShopName = defaultValues?.metadata?.shopName || watchedShopUrl || t('shopify.yourStore');

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Connection Status + Setup */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left: Connection */}
        <Card className="flex h-full flex-col">
          <CardHeader>
            <CardTitle>{t('shopify.connectTitle')}</CardTitle>
            <CardDescription>
              {isConnected
                ? t('shopify.connectedTo', { shopName: connectedShopName })
                : t('shopify.connectDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col space-y-4">
            {/* Connected Status */}
            {isConnected && storeId && (
              <div className="space-y-3">
                <ShopifyCredentialsBanner
                  storeId={storeId}
                  shopUrl={watchedShopUrl}
                  canReconnect={canUpdateOAuthIntegration}
                  onReconnect={handleUpdateIntegration}
                  connectedShopName={connectedShopName}
                  connectedViaOAuth={connectedViaOAuth}
                />
                <div className="flex flex-wrap gap-2">
                  {canUpdateOAuthIntegration && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleUpdateIntegration}
                      disabled={isLoading}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t('shopify.updateIntegration')}
                    </Button>
                  )}
                  {isShopifyScriptPlugged && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                      onClick={handleUnplugScript}
                      disabled={isUnplugging || isLoading}
                    >
                      <Unplug className="mr-2 h-4 w-4" />
                      {isUnplugging ? t('shopify.unplugging') : t('shopify.unplug')}
                    </Button>
                  )}
                  {onDisconnect && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={async () => {
                        if (window.confirm(t('shopify.disconnectConfirm'))) {
                          setIsDisconnecting(true);
                          try {
                            await onDisconnect();
                          } finally {
                            setIsDisconnecting(false);
                          }
                        }
                      }}
                      disabled={isDisconnecting || isLoading}
                    >
                      {isDisconnecting ? t('shopify.disconnecting') : t('shopify.disconnect')}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* OAuth Connect Option (if enabled and not connected) */}
            {isOAuthEnabled && !isConnected && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="oauthClientId">
                    {t('shopify.clientIdLabel')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="oauthClientId"
                    placeholder={t('shopify.clientIdPlaceholder')}
                    value={oauthClientId}
                    onChange={(e) => setOauthClientId(e.target.value)}
                    disabled={isLoading}
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="oauthClientSecret">
                    {t('shopify.clientSecretLabel')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="oauthClientSecret"
                    type="password"
                    placeholder={t('shopify.clientSecretPlaceholder')}
                    value={oauthClientSecret}
                    onChange={(e) => setOauthClientSecret(e.target.value)}
                    disabled={isLoading}
                    autoComplete="new-password"
                    data-1p-ignore
                    data-lpignore="true"
                  />
                  <p className="text-muted-foreground text-xs">
                    {t('shopify.clientCredentialsHelp')}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="oauthShopUrl">
                    {t('shopify.storeUrlLabel')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="oauthShopUrl"
                    placeholder={t('shopify.storeUrlPlaceholder')}
                    value={oauthShopUrl}
                    onChange={(e) => setOauthShopUrl(e.target.value)}
                    disabled={isLoading}
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                    aria-invalid={oauthShopUrlInvalid || undefined}
                  />
                  <p className="text-muted-foreground text-xs">
                    {t('shopify.storeUrlHelp')}
                  </p>
                  {oauthShopUrlInvalid && (
                    <p className="text-destructive text-xs">
                      {t('shopify.validation.shopUrlInvalid')}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  className="w-full bg-[#96bf48] hover:bg-[#7a9e3a]"
                  onClick={handleConnectWithOAuth}
                  disabled={
                    !oauthShopUrl ||
                    oauthShopUrlInvalid ||
                    !oauthClientId ||
                    !oauthClientSecret ||
                    !storeId ||
                    !accountId ||
                    isLoading
                  }
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t('shopify.connectWithShopify')}
                </Button>
                <p className="text-muted-foreground text-center text-xs">
                  {t('shopify.redirectNotice')}
                </p>
              </div>
            )}

            {/* Manual Setup Toggle */}
            {!isConnected && (
              <div className="border-t pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-between"
                  onClick={() => setShowManualSetup(!showManualSetup)}
                >
                  <span className="text-muted-foreground text-sm">
                    {isOAuthEnabled ? (
                      <>
                        {t('shopify.manualSetupLegacy')}{' '}
                        <span className="text-xs text-amber-600">{t('shopify.manualSetupLegacyLabel')}</span>
                      </>
                    ) : (
                      t('shopify.setupWithToken')
                    )}
                  </span>
                  {showManualSetup ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </div>
            )}

            {/* Manual Credentials Form (shown if not OAuth or toggled) */}
            {(showManualSetup || !isOAuthEnabled || isConnected) && (
              <div className="space-y-4">
                {!isConnected && isOAuthEnabled && (
                  <Alert variant="destructive" className="border-amber-500 bg-amber-50 text-amber-900">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-sm">
                      <strong className="mb-1 block">⚠️ {t('shopify.legacyDeprecated')}</strong>
                      <span className="mb-2 block">
                        {t('shopify.legacyDeprecationMessage')}{' '}
                        <strong>{t('shopify.legacyDeprecationDate')}</strong>
                        {t('shopify.legacyDeprecationSuffix')}
                      </span>
                      <span className="block text-xs text-amber-700">
                        {t('shopify.legacyRecommendation')}
                      </span>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="shopUrl">
                    {t('shopify.shopUrlLabel')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="shopUrl"
                    placeholder={t('shopify.shopUrlPlaceholder')}
                    disabled={isLoading}
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                    {...register('shopUrl')}
                  />
                  <p className="text-muted-foreground text-xs">{t('shopify.shopUrlFormat')}</p>
                  {errors.shopUrl && <p className="text-destructive text-xs">{errors.shopUrl.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="apiToken">
                    {t('shopify.apiTokenLabel')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="apiToken"
                    type="password"
                    placeholder={t('shopify.apiTokenPlaceholder')}
                    disabled={isLoading}
                    autoComplete="new-password"
                    data-1p-ignore
                    data-lpignore="true"
                    {...register('apiToken')}
                  />
                  <p className="text-muted-foreground text-xs">{t('shopify.apiTokenHelp')}</p>
                  {errors.apiToken && <p className="text-destructive text-xs">{errors.apiToken.message}</p>}
                </div>

                {/* Optional: Webhook Credentials */}
                <div className="border-t pt-4">
                  <p className="text-muted-foreground mb-3 text-xs">
                    {t('shopify.webhookOptional')}
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="apiKey" className="text-sm">
                        {t('shopify.apiKeyLabel')}
                      </Label>
                      <Input
                        id="apiKey"
                        placeholder={t('shopify.apiKeyPlaceholder')}
                        disabled={isLoading}
                        autoComplete="off"
                        data-1p-ignore
                        data-lpignore="true"
                        {...register('apiKey')}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="apiSecretKey" className="text-sm">
                        {t('shopify.apiSecretLabel')}
                      </Label>
                      <Input
                        id="apiSecretKey"
                        type="password"
                        placeholder={t('shopify.apiSecretPlaceholder')}
                        disabled={isLoading}
                        autoComplete="new-password"
                        data-1p-ignore
                        data-lpignore="true"
                        {...register('apiSecretKey')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Webhooks & Audit */}
            {isConnected && (
              <div className="space-y-3">
                {/* Audit Scopes */}
                <div className="bg-muted flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleAuditScopes}
                    disabled={checkScopesQuery.isLoading || isLoading}
                    className={cn('shrink-0', showScopeAudit ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : '')}
                  >
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    {t('shopify.auditScopes')}
                  </Button>
                  <span className="text-muted-foreground text-xs">{t('shopify.auditScopesHelp')}</span>
                </div>

                {/* Scope Audit Results */}
                {showScopeAudit && (
                  <div className="rounded-md border bg-gray-50 p-3 shadow-sm">
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="flex items-center gap-2 text-sm font-semibold">
                        <ShieldCheck className="h-4 w-4 text-blue-600" />
                        {t('shopify.scopeAuditResults')}
                      </h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowScopeAudit(false)}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>

                    {checkScopesQuery.isLoading ? (
                      <div className="flex items-center gap-2 py-2">
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        <p className="text-xs text-gray-500">{t('shopify.checkingScopes')}</p>
                      </div>
                    ) : checkScopesQuery.error ? (
                      <div className="rounded-md bg-red-50 p-2 text-xs text-red-600">
                        {t('shopify.scopeCheckFailed', { message: checkScopesQuery.error.message })}
                      </div>
                    ) : checkScopesQuery.data ? (
                      <div className="space-y-3">
                        {/* Summary */}
                        <div
                          className={cn(
                            'flex items-center gap-2 rounded-md p-2 text-xs',
                            checkScopesQuery.data.status === 'ok'
                              ? 'bg-green-50 text-green-700'
                              : 'bg-red-50 text-red-700',
                          )}
                        >
                          {checkScopesQuery.data.status === 'ok' ? (
                            <>
                              <Check className="h-4 w-4 shrink-0" />
                              <span className="font-medium">
                                {t('shopify.scopesOk', {
                                  count: checkScopesQuery.data.required.length,
                                })}
                              </span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="h-4 w-4 shrink-0" />
                              <span className="font-medium">
                                {t('shopify.scopesMissing', {
                                  missing: checkScopesQuery.data.missing.length,
                                  total: checkScopesQuery.data.required.length,
                                })}
                              </span>
                            </>
                          )}
                        </div>

                        {/* Scope breakdown */}
                        <div className="grid grid-cols-2 gap-1">
                          {checkScopesQuery.data.required.map((scope: string) => {
                            const isGranted = checkScopesQuery.data.granted.includes(scope);
                            return (
                              <div
                                key={scope}
                                className={cn(
                                  'flex items-center gap-1.5 rounded px-2 py-1 text-[10px] font-mono',
                                  isGranted ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700',
                                )}
                              >
                                {isGranted ? (
                                  <Check className="h-3 w-3 shrink-0" />
                                ) : (
                                  <X className="h-3 w-3 shrink-0" />
                                )}
                                <span className="truncate">{scope}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: Legacy Tutorial (show only for manual setup) */}
        {(showManualSetup || isConnected) && (
          <ShopifySetupTutorial defaultExpanded={!isConnected} variant="legacy" className="h-full" />
        )}

        {/* Right: OAuth Tutorial (when OAuth enabled and not showing manual) */}
        {isOAuthEnabled && !showManualSetup && !isConnected && (
          <ShopifySetupTutorial defaultExpanded variant="oauth" className="h-full" />
        )}
      </div>

      {/* Order Sync Settings */}
      <Card>
        <CardHeader>
          <CardTitle>{t('shopify.orderSyncTitle')}</CardTitle>
          <CardDescription>{t('shopify.orderSyncDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="syncOrders"
              checked={watch('syncOrders')}
              onCheckedChange={(checked) => setValue('syncOrders', checked as boolean)}
              disabled={isLoading}
              className="mt-1"
            />
            <div className="space-y-1">
              <Label htmlFor="syncOrders" className="cursor-pointer font-medium">
                {t('shopify.syncOrders')}
              </Label>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="syncPresentmentCurrencies"
              checked={watch('syncPresentmentCurrencies')}
              onCheckedChange={(checked) => setValue('syncPresentmentCurrencies', checked as boolean)}
              disabled={isLoading}
              className="mt-1"
            />
            <div className="space-y-1">
              <Label htmlFor="syncPresentmentCurrencies" className="cursor-pointer font-medium">
                {t('shopify.syncPresentmentCurrencies')}
              </Label>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="syncInventory"
              checked={watch('syncInventory')}
              onCheckedChange={(checked) => setValue('syncInventory', checked as boolean)}
              disabled={isLoading || watch('useGraphqlOrderInjection')}
              className="mt-1"
            />
            <div className="space-y-1">
              <Label
                htmlFor="syncInventory"
                className={cn('font-medium', !watch('useGraphqlOrderInjection') && 'cursor-pointer')}
              >
                {t('shopify.syncInventory')}
              </Label>
              <p className="text-muted-foreground text-sm">
                {watch('useGraphqlOrderInjection')
                  ? t('shopify.syncInventoryGraphqlDisabled')
                  : t('shopify.syncInventoryHelp')}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="useGraphqlOrderInjection"
              checked={watch('useGraphqlOrderInjection')}
              onCheckedChange={(checked) => {
                const isChecked = checked as boolean;
                setValue('useGraphqlOrderInjection', isChecked);
                if (isChecked) {
                  setValue('syncInventory', false);
                }
                // GraphQL order injection relies on write_draft_orders.
                // Surface missing scopes immediately so the merchant knows to re-install.
                if (isChecked && isConnected) {
                  void runScopeAudit({ silent: true });
                }
              }}
              disabled={isLoading}
              className="mt-1"
            />
            <div className="space-y-1">
              <Label htmlFor="useGraphqlOrderInjection" className="cursor-pointer font-medium">
                {t('shopify.useGraphqlOrderInjection')}
              </Label>
              <p className="text-muted-foreground text-sm">{t('shopify.useGraphqlOrderInjectionHelp')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Notifications */}
      <Card>
        <CardHeader>
          <CardTitle>{t('shopify.emailNotificationsTitle')}</CardTitle>
          <CardDescription>{t('shopify.emailNotificationsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="sendReceipt"
              checked={watch('sendReceipt')}
              onCheckedChange={(checked) => setValue('sendReceipt', checked as boolean)}
              disabled={isLoading}
              className="mt-1"
            />
            <div className="space-y-1">
              <Label htmlFor="sendReceipt" className="cursor-pointer font-medium">
                {t('shopify.sendReceipt')}
              </Label>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="sendFulfillmentReceipt"
              checked={watch('sendFulfillmentReceipt')}
              onCheckedChange={(checked) => setValue('sendFulfillmentReceipt', checked as boolean)}
              disabled={isLoading}
              className="mt-1"
            />
            <div className="space-y-1">
              <Label htmlFor="sendFulfillmentReceipt" className="cursor-pointer font-medium">
                {t('shopify.sendFulfillmentReceipt')}
              </Label>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="sendRebillReceipt"
              checked={watch('sendRebillReceipt')}
              onCheckedChange={(checked) => setValue('sendRebillReceipt', checked as boolean)}
              disabled={isLoading}
              className="mt-1"
            />
            <div className="space-y-1">
              <Label htmlFor="sendRebillReceipt" className="cursor-pointer font-medium">
                {t('shopify.sendRebillReceipt')}
              </Label>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="sendRebillFulfillmentReceipt"
              checked={watch('sendRebillFulfillmentReceipt')}
              onCheckedChange={(checked) => setValue('sendRebillFulfillmentReceipt', checked as boolean)}
              disabled={isLoading}
              className="mt-1"
            />
            <div className="space-y-1">
              <Label htmlFor="sendRebillFulfillmentReceipt" className="cursor-pointer font-medium">
                {t('shopify.sendRebillFulfillmentReceipt')}
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Features */}
      <Card>
        <CardHeader>
          <CardTitle>{t('shopify.advancedFeaturesTitle')}</CardTitle>
          <CardDescription>{t('shopify.advancedFeaturesDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dynamic Discounts */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="dynamicDiscountEnabled"
                checked={dynamicDiscountEnabled}
                onCheckedChange={(checked) => setValue('dynamicDiscountEnabled', checked as boolean)}
                disabled={isLoading}
                className="mt-1"
              />
              <div className="space-y-1">
                <Label htmlFor="dynamicDiscountEnabled" className="cursor-pointer font-medium">
                  {t('shopify.dynamicDiscount')}
                </Label>
              </div>
            </div>

            {dynamicDiscountEnabled && (
              <div className="ml-7 space-y-2">
                <Label htmlFor="dynamicDiscountStrategy">{t('shopify.dynamicDiscountStrategy')}</Label>
                <Select
                  value={watch('dynamicDiscountStrategy')}
                  onValueChange={(value) =>
                    setValue('dynamicDiscountStrategy', value as 'order-level' | 'line-item-allocation')
                  }
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('shopify.selectDiscountStrategy')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="order-level">{t('shopify.orderLevelDiscounts')}</SelectItem>
                    <SelectItem value="line-item-allocation">{t('shopify.lineItemAllocation')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground mt-1 text-sm">
                  {t('shopify.discountStrategyHelp')}
                </p>
              </div>
            )}
          </div>

          {/* Dynamic Selling Plans */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="dynamicSellingPlanEnabled"
                checked={dynamicSellingPlanEnabled}
                onCheckedChange={(checked) => setValue('dynamicSellingPlanEnabled', checked as boolean)}
                disabled={isLoading}
                className="mt-1"
              />
              <div className="space-y-1">
                <Label htmlFor="dynamicSellingPlanEnabled" className="cursor-pointer font-medium">
                  {t('shopify.dynamicSellingPlans')}
                </Label>
              </div>
            </div>

            {dynamicSellingPlanEnabled && (
              <div className="bg-muted ml-7 rounded-lg border p-4">
                <div className="space-y-2">
                  <h4 className="font-medium">{t('shopify.dynamicSellingPlansTitle')}</h4>
                  <p className="text-muted-foreground text-sm">{t('shopify.dynamicSellingPlansHelp')}</p>
                  <p className="text-muted-foreground text-sm">{t('shopify.dynamicSellingPlansHelp2')}</p>
                </div>
              </div>
            )}
          </div>

          {/* Dynamic Price Sets */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="dynamicPriceSet"
                checked={dynamicPriceSet}
                onCheckedChange={(checked) => setValue('dynamicPriceSet', checked as boolean)}
                disabled={isLoading}
                className="mt-1"
              />
              <div className="space-y-1">
                <Label htmlFor="dynamicPriceSet" className="cursor-pointer font-medium">
                  {t('shopify.dynamicPriceSet')}
                </Label>
              </div>
            </div>

            {dynamicPriceSet && (
              <div className="bg-muted ml-7 rounded-lg border p-4">
                <div className="space-y-2">
                  <h4 className="font-medium">{t('shopify.dynamicPriceSetTitle')}</h4>
                  <p className="text-muted-foreground text-sm">{t('shopify.dynamicPriceSetHelp')}</p>
                  <p className="text-muted-foreground text-sm">{t('shopify.dynamicPriceSetHelp2')}</p>
                </div>
              </div>
            )}
          </div>

          {/* Auto Sync */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="autoSyncEnabled"
                checked={autoSyncEnabled}
                onCheckedChange={(checked) => setValue('autoSyncEnabled', checked as boolean)}
                disabled={isLoading}
                className="mt-1"
              />
              <div className="space-y-1">
                <Label htmlFor="autoSyncEnabled" className="cursor-pointer font-medium">
                  {t('shopify.autoSync')}
                </Label>
              </div>
            </div>

            {autoSyncEnabled && (
              <div className="bg-muted ml-7 rounded-lg border p-4">
                <div className="space-y-2">
                  <h4 className="font-medium">{t('shopify.autoSyncTitle')}</h4>
                  <p className="text-muted-foreground text-sm">{t('shopify.autoSyncHelp')}</p>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="autoSyncFrequency" className="text-sm font-medium">
                      {t('shopify.syncFrequency')}
                    </Label>
                    <Input
                      id="autoSyncFrequency"
                      name="autoSyncFrequency"
                      value="6 hours"
                      disabled
                      className="bg-muted w-32"
                    />
                    <span className="text-muted-foreground text-xs">{t('shopify.fixedInterval')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Wetracked Integration */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="enableWetracked"
                checked={watch('enableWetracked')}
                onCheckedChange={(checked) => setValue('enableWetracked', checked as boolean)}
                disabled={isLoading}
                className="mt-1"
              />
              <div className="space-y-1">
                <Label htmlFor="enableWetracked" className="cursor-pointer font-medium">
                  {t('shopify.enableWetracked')}
                </Label>
                <p className="text-muted-foreground text-sm">{t('shopify.wetrackedHelp')}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <ShopifyScopeUpdateTutorial
        open={isScopeUpdateModalOpen}
        onOpenChange={setIsScopeUpdateModalOpen}
        onUpdateIntegration={handleUpdateIntegration}
        canUpdateIntegration={canUpdateOAuthIntegration}
        missingScopes={checkScopesQuery.data?.missing ?? []}
      />

      {/* OAuth Connecting Loader Dialog */}
      <Dialog open={isOAuthConnecting}>
        <DialogContent className="sm:max-w-sm" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#96bf48] border-t-transparent" />
              {t('shopify.oauthConnectingTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('shopify.oauthConnectingDescription')}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </form>
  );
}
